// src/pages/ContactMe.tsx
import React, { useEffect, useState } from 'react';
import './ContactMe.css';
import { FaEnvelope, FaPhoneAlt, FaLinkedin, FaGithub, FaMapMarkerAlt, FaDownload, FaStar, FaPaperPlane, FaExternalLinkAlt } from 'react-icons/fa';
import { getContactMe } from '../queries/getContactMe';
import emailjs from 'emailjs-com';

// Fallback data used when API is unavailable
const FALLBACK_DATA = {
  name: "Mantavya Mahajan",
  title: "Software Engineer - Backend (Parafin, Spends team)",
  summary: "Backend-focused software engineer with experience across AI, full-stack development, and research—now building reliable systems primarily in Python and Scala.",
  companyUniversity: "Penn State University",
  linkedinLink: "https://www.linkedin.com/in/mantavya-mahajan-42972721b/",
  email: "mantavyamahajan08@gmail.com",
  phoneNumber: "+1 (564) 544-4219",
  location: "State College, Pennsylvania",
  githubLink: "https://github.com/mantavya0807",
  resumeLink: "/docs/resume.pdf" // Put your resume in public/assets folder
};

const ContactMe: React.FC = () => {
  const [userData, setUserData] = useState<any>(FALLBACK_DATA);
  const [activeTab, setActiveTab] = useState<'contact' | 'review'>('contact');
  const [rating, setRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [feedbackMessage, setFeedbackMessage] = useState<string>("");
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showRatingError, setShowRatingError] = useState<boolean>(false);
  
  // Rating statistics
  const [ratingStats, setRatingStats] = useState({
    average: 4.8,
    total: 24,
    breakdown: [0, 0, 1, 5, 18] // Stars 1-5, index 0-4
  });

  useEffect(() => {
    async function fetchUserData() {
      try {
        const data = await getContactMe();
        setUserData({...FALLBACK_DATA, ...data});
      } catch (error) {
        console.warn("Error fetching contact data, using fallback data:", error);
        setUserData(FALLBACK_DATA);
      }
    }

    fetchUserData();
  }, []);

  // EmailJS configuration - Uses environment variables
  const EMAILJS_CONFIG = {
    SERVICE_ID: process.env.REACT_APP_EMAILJS_SERVICE_ID || '',
    TEMPLATE_ID: process.env.REACT_APP_EMAILJS_TEMPLATE_ID || '',
    USER_ID: process.env.REACT_APP_EMAILJS_USER_ID || ''
  };

  const handleRatingHover = (index: number) => {
    setHoveredRating(index);
  };

  const handleRatingClick = (index: number) => {
    setRating(index);
  };

  const handleSubmitFeedback = async (e: React.FormEvent) => {
    e.preventDefault();
    if (rating === 0) {
      setShowRatingError(true);
      setTimeout(() => setShowRatingError(false), 3000); // Hide error after 3 seconds
      return;
    }

    // Clear any existing error
    setShowRatingError(false);

    // Check if EmailJS is configured
    if (!EMAILJS_CONFIG.SERVICE_ID || !EMAILJS_CONFIG.TEMPLATE_ID || !EMAILJS_CONFIG.USER_ID) {
      console.warn('EmailJS not configured. Feedback will only update local state.');
      // Just update local state if EmailJS isn't configured
      const newBreakdown = [...ratingStats.breakdown];
      newBreakdown[rating - 1] += 1;
      
      const newTotal = ratingStats.total + 1;
      const newAverage = parseFloat(((ratingStats.average * ratingStats.total + rating) / newTotal).toFixed(1));
      
      setRatingStats({
        average: newAverage,
        total: newTotal,
        breakdown: newBreakdown
      });
      
      setSubmitted(true);
      setTimeout(() => {
        setRating(0);
        setFeedbackMessage("");
        setSubmitted(false);
      }, 3000);
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Prepare template parameters for EmailJS
      const templateParams = {
        name: 'Portfolio Visitor',
        time: new Date().toLocaleString(),
        rating: rating,
        message: feedbackMessage || 'No additional message provided',
        stars: '★'.repeat(rating) + '☆'.repeat(5 - rating)
      };

      // Send email via EmailJS
      await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        templateParams,
        EMAILJS_CONFIG.USER_ID
      );

      console.log('Feedback email sent successfully!');
      
      // Update the rating statistics (for UI feedback)
      const newBreakdown = [...ratingStats.breakdown];
      newBreakdown[rating - 1] += 1;
      
      const newTotal = ratingStats.total + 1;
      
      // Calculate new average: (old_avg * old_total + new_rating) / new_total
      const newAverage = parseFloat(((ratingStats.average * ratingStats.total + rating) / newTotal).toFixed(1));
      
      setRatingStats({
        average: newAverage,
        total: newTotal,
        breakdown: newBreakdown
      });
      
      setSubmitted(true);
      
      // Reset form after a delay
      setTimeout(() => {
        setRating(0);
        setFeedbackMessage("");
        setSubmitted(false);
      }, 3000);

    } catch (error) {
      console.error('Failed to send feedback email:', error);
      alert('Sorry, there was an error sending your feedback. Please try again or contact me directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate percentage for each star rating
  const calculatePercentage = (count: number) => {
    return `${Math.round((count / ratingStats.total) * 100)}%`;
  };

  return (
    <div className="netflix-contact-container">
      {/* Netflix-style Banner */}
      <div className="netflix-hero">
        <div className="hero-content">
          <div className="hero-profile">
            {/* Add your profile picture here */}
            <img 
              src="/docs/profile.jpg" 
              alt="Mantavya Mahajan" 
              className="profile-image" 
            />
            <div className="vignette-overlay"></div>
          </div>
          <div className="hero-text">
            <h1 className="hero-title">{userData.name}</h1>
            <p className="hero-tagline">{userData.title}</p>
            <div className="hero-meta">
              <span className="netflix-badge">CS GRAD</span>
              <span className="netflix-badge secondary">PARAFIN • SPENDS</span>
              <span className="netflix-badge match">98% MATCH</span>
            </div>
            <p className="hero-description">{userData.summary}</p>
          </div>
        </div>
      </div>

      {/* Netflix-style Tab Navigation */}
      <div className="netflix-tabs">
        <button 
          className={`tab-button ${activeTab === 'contact' ? 'active' : ''}`}
          onClick={() => setActiveTab('contact')}
        >
          Contact Information
        </button>
        <button 
          className={`tab-button ${activeTab === 'review' ? 'active' : ''}`}
          onClick={() => setActiveTab('review')}
        >
          Reviews & Ratings
        </button>
      </div>

      {/* Contact Information Tab */}
      {activeTab === 'contact' && (
        <div className="netflix-section contact-tab">
          <h2 className="netflix-section-title">
            <span className="title-decoration"></span>
            Contact Details
          </h2>
          
          <div className="contact-skills-grid">
            <div className="contact-skill-card">
              <div className="skill-icon">
                <FaEnvelope />
              </div>
              <div className="skill-details">
                <div className="skill-match">98% Match</div>
                <h3 className="skill-name">Email</h3>
                <p className="skill-description">
                  <a href={`mailto:${userData.email}`} className="info-link">{userData.email}</a>
                </p>
              </div>
            </div>

            <div className="contact-skill-card">
              <div className="skill-icon">
                <FaPhoneAlt />
              </div>
              <div className="skill-details">
                <div className="skill-match">96% Match</div>
                <h3 className="skill-name">Phone</h3>
                <p className="skill-description">
                  <a href={`tel:${userData.phoneNumber}`} className="info-link">{userData.phoneNumber}</a>
                </p>
              </div>
            </div>

            <div className="contact-skill-card">
              <div className="skill-icon">
                <FaMapMarkerAlt />
              </div>
              <div className="skill-details">
                <div className="skill-match">94% Match</div>
                <h3 className="skill-name">Location</h3>
                <p className="skill-description">{userData.location}</p>
              </div>
            </div>

            <div className="contact-skill-card">
              <div className="skill-icon">
                <FaGithub />
              </div>
              <div className="skill-details">
                <div className="skill-match">97% Match</div>
                <h3 className="skill-name">GitHub</h3>
                <p className="skill-description">
                  <a href={userData.githubLink} target="_blank" rel="noopener noreferrer" className="info-link">
                    View Projects <FaExternalLinkAlt className="link-icon" />
                  </a>
                </p>
              </div>
            </div>
            
            <div className="contact-skill-card">
              <div className="skill-icon">
                <FaDownload />
              </div>
              <div className="skill-details">
                <div className="skill-match">99% Match</div>
                <h3 className="skill-name">Resume</h3>
                <p className="skill-description">
                  <a href={userData.resumeLink} target="_blank" rel="noopener noreferrer" className="info-link">
                    Download Resume <FaExternalLinkAlt className="link-icon" />
                  </a>
                </p>
              </div>
            </div>
            
            <div className="contact-skill-card">
              <div className="skill-icon">
                <FaLinkedin />
              </div>
              <div className="skill-details">
                <div className="skill-match">95% Match</div>
                <h3 className="skill-name">LinkedIn</h3>
                <p className="skill-description">
                  <a href={userData.linkedinLink} target="_blank" rel="noopener noreferrer" className="info-link">
                    View Profile <FaExternalLinkAlt className="link-icon" />
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reviews Tab - Netflix style review section */}
      {activeTab === 'review' && (
        <div className="netflix-section review-tab">
          <h2 className="netflix-section-title">
            <span className="title-decoration"></span>
            Reviews & Ratings
          </h2>
          
          <div className="netflix-reviews-container">
            <div className="reviews-header">
              <div className="average-rating">
                <div className="rating-number">{ratingStats.average}</div>
                <div className="rating-stars">
                  {[1, 2, 3, 4, 5].map((index) => {
                    if (index <= Math.floor(ratingStats.average)) {
                      return <FaStar key={index} />;
                    } else if (index === Math.ceil(ratingStats.average) && ratingStats.average % 1 !== 0) {
                      return <FaStar key={index} className="half-star" />;
                    } else {
                      return <FaStar key={index} className="empty-star" />;
                    }
                  })}
                </div>
                <div className="total-reviews">Based on {ratingStats.total} reviews</div>
              </div>
              
              <div className="rating-breakdown">
                {ratingStats.breakdown.map((count, index) => {
                  const starNumber = 5 - index; // 5, 4, 3, 2, 1 stars
                  return (
                    <div className="breakdown-item" key={starNumber}>
                      <span className="breakdown-label">{starNumber} Stars</span>
                      <div className="breakdown-bar">
                        <div 
                          className="breakdown-fill" 
                          style={{width: calculatePercentage(count)}}
                        ></div>
                      </div>
                      <span className="breakdown-count">{count}</span>
                    </div>
                  );
                }).reverse()} {/* Reverse to show 5 stars first */}
              </div>
            </div>
            
            <div className="netflix-review-form-container">
              <h3 className="review-form-title">Rate This Portfolio</h3>
              
              {submitted ? (
                <div className="feedback-thanks">
                  <div className="thanks-animation"></div>
                  <h3>Thanks for your feedback!</h3>
                  <p>Your review has been submitted successfully and I've been notified!</p>
                </div>
              ) : (
                <form className="netflix-review-form" onSubmit={handleSubmitFeedback}>
                  <div className="star-rating">
                    {[1, 2, 3, 4, 5].map((index) => (
                      <span 
                        key={index}
                        className={`rating-star ${index <= (hoveredRating || rating) ? 'active' : ''}`}
                        onMouseEnter={() => handleRatingHover(index)}
                        onMouseLeave={() => handleRatingHover(0)}
                        onClick={() => handleRatingClick(index)}
                      >
                        <FaStar />
                      </span>
                    ))}
                  </div>
                  
                  {/* Custom Rating Error Message */}
                  {showRatingError && (
                    <div className="rating-error-message">
                      <span>⚠️ Please select a rating before submitting</span>
                    </div>
                  )}
                  
                  <div className="feedback-input-container">
                    <textarea 
                      className="netflix-input"
                      placeholder="Share your thoughts about this portfolio..."
                      value={feedbackMessage}
                      onChange={(e) => setFeedbackMessage(e.target.value)}
                    />
                  </div>
                  
                  <button type="submit" className="netflix-button primary submit-button" disabled={isSubmitting}>
                    <FaPaperPlane /> {isSubmitting ? 'Sending...' : 'Send Feedback'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Netflix-style Footer - with LinkedIn link */}
      <div className="netflix-footer">
        <div className="footer-content">
          <p className="available-statement">Open to connecting about backend engineering, Python/Scala, and building reliable systems.</p>
          <div className="footer-links">
            <a href={userData.linkedinLink} target="_blank" rel="noopener noreferrer">LinkedIn</a>
            <a href={userData.githubLink} target="_blank" rel="noopener noreferrer">GitHub</a>
            <a href={userData.resumeLink} target="_blank" rel="noopener noreferrer">Resume</a>
          </div>
          <div className="footer-credits">
            <span>© {new Date().getFullYear()} {userData.name}</span>
            <span>Inspired by Netflix</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactMe;